import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";
import type { UserProfile } from "@/mocks/data";

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

const normativaItems = [
  {
    id: "n1",
    titulo: "Ley 20.536 sobre Violencia Escolar",
    tipo: "Ley",
    numero: "20.536",
    fecha: "2011",
    resumen: "Define violencia escolar, establece obligaciones para establecimientos y crea los Comités de Buena Convivencia Escolar.",
    articulosClave: ["Art. 16A: Definición de violencia escolar", "Art. 16B: Medidas disciplinarias", "Art. 16D: Responsabilidades del establecimiento"],
    url: "https://www.bcn.cl/leychile/navegar?idNorma=1028723"
  },
  {
    id: "n2",
    titulo: "Ley 21.128 Aula Segura",
    tipo: "Ley",
    numero: "21.128",
    fecha: "2018",
    resumen: "Fortalece facultades de directores para expulsar o cancelar matrícula en casos graves que afecten la seguridad.",
    articulosClave: ["Art. 6: Nuevas facultades del director", "Art. 6 bis: Procedimiento de expulsión", "Art. 6 ter: Recursos y apelaciones"],
    url: "https://www.bcn.cl/leychile/navegar?idNorma=1126786"
  },
  {
    id: "n3",
    titulo: "Ley 20.370 General de Educación",
    tipo: "Ley",
    numero: "20.370",
    fecha: "2009",
    resumen: "Marco general del sistema educativo chileno. Establece derechos, deberes y principios del sistema educacional.",
    articulosClave: ["Art. 10: Derechos de los estudiantes", "Art. 11: Deberes del establecimiento", "Art. 46: Reglamento interno"],
    url: "https://www.bcn.cl/leychile/navegar?idNorma=1006043"
  },
  {
    id: "n4",
    titulo: "Orientaciones MINEDUC Convivencia Escolar",
    tipo: "Orientación",
    numero: "MINEDUC 2024",
    fecha: "2024",
    resumen: "Lineamientos oficiales para elaboración, implementación y actualización de Reglamentos Internos de Convivencia Escolar.",
    articulosClave: ["Cap. 2: Estructura del RICE", "Cap. 3: Enfoque formativo", "Cap. 5: Protocolos de actuación obligatorios"],
    url: "https://convivenciaescolar.mineduc.cl/"
  },
  {
    id: "n5",
    titulo: "Circular N°1 Superintendencia de Educación",
    tipo: "Circular",
    numero: "Circ. 1/2025",
    fecha: "2025",
    resumen: "Instrucciones sobre aplicación de medidas disciplinarias, debido proceso y registro de actuaciones.",
    articulosClave: ["Sección II: Debido proceso", "Sección III: Registro de medidas", "Sección IV: Fiscalización"],
    url: "https://www.supereduc.cl/"
  },
];

export default function NormativaPage() {
  const navigate = useNavigate();
  const [user] = useState<UserProfile | null>(getStoredUser);
  const [search, setSearch] = useState("");

  if (!user) return null;

  const filtered = normativaItems.filter(
    (n) =>
      n.titulo.toLowerCase().includes(search.toLowerCase()) ||
      n.resumen.toLowerCase().includes(search.toLowerCase())
  );

  const handleDownloadSimulate = (title: string) => {
    const element = document.createElement("a");
    const file = new Blob([`Copia oficial referencial de: ${title}`], { type: 'text/plain;charset=utf-8' });
    element.href = URL.createObjectURL(file);
    element.download = `${title.replace(/\s+/g, "_")}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4">
      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <Input
          placeholder="Buscar en normativa nacional..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<i className="ri-search-line" />}
          className="max-w-md"
          fullWidth={false}
        />
        <div className="flex gap-2">
          <Badge variant="primary">Leyes</Badge>
          <Badge variant="accent">Orientaciones</Badge>
          <Badge variant="neutral">Circulares</Badge>
        </div>
      </div>

      {/* Quick consult CTA */}
      <Card className="bg-primary-50 border-primary-200">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-robot-line text-white text-lg" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-primary-800">Consulta la normativa nacional con IA</h3>
            <p className="text-xs text-primary-600 mt-0.5">
              Haz preguntas sobre leyes y decretos chilenos y recibe respuestas con cita normativa.
            </p>
          </div>
          <Button 
            onClick={() => navigate("/ia/consulta?q=" + encodeURIComponent("¿Cómo se relaciona la normativa nacional con los protocolos de convivencia de nuestro establecimiento?"))}
            size="sm"
            className="cursor-pointer"
          >
            <i className="ri-chat-3-line mr-1" /> Consultar ahora
          </Button>
        </div>
      </Card>

      {/* Normatives grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((item) => (
          <Card key={item.id}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-scales-line text-primary-600 text-lg" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant={item.tipo === "Ley" ? "primary" : item.tipo === "Orientación" ? "accent" : "neutral"} size="sm">
                    {item.tipo} {item.numero}
                  </Badge>
                  <span className="text-xs text-foreground-500">{item.fecha}</span>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900">{item.titulo}</h3>
              </div>
            </div>
            <p className="text-sm text-foreground-600 mb-3">{item.resumen}</p>

            <div className="bg-background-50 rounded-lg p-3 mb-3">
              <p className="text-xs font-medium text-foreground-700 mb-2">Artículos clave:</p>
              <div className="space-y-1">
                {item.articulosClave.map((art, i) => (
                  <div key={i} className="text-xs text-foreground-600 flex items-start gap-1.5">
                    <i className="ri-link text-foreground-400 mt-0.5" />
                    {art}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-background-200">
              <Button 
                onClick={() => window.open(item.url, "_blank")}
                variant="ghost" 
                size="sm"
                className="cursor-pointer"
              >
                <i className="ri-eye-line mr-1" /> Ver documento
              </Button>
              <Button 
                onClick={() => handleDownloadSimulate(item.titulo)}
                variant="ghost" 
                size="sm"
                className="cursor-pointer"
              >
                <i className="ri-download-line mr-1" /> Descargar
              </Button>
              <Button 
                onClick={() => navigate("/ia/consulta?q=" + encodeURIComponent(`¿Qué obligaciones y regulaciones establece la ${item.titulo} en el marco de la convivencia escolar?`))}
                variant="ghost" 
                size="sm"
                className="cursor-pointer"
              >
                <i className="ri-robot-line mr-1" /> Consultar IA
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Related warning */}
      <Card className="border-secondary-200 bg-secondary-50/50">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-secondary-500 text-lg mt-0.5" />
          <div>
            <p className="text-sm font-medium text-secondary-800">Aviso importante</p>
            <p className="text-xs text-secondary-600 mt-1">
              La información normativa presentada es de carácter referencial. Para efectos legales,
              se debe consultar directamente la publicación oficial en el Diario Oficial de Chile o
              en el sitio web de la Biblioteca del Congreso Nacional (www.bcn.cl).
              Ezer Convivencia360 no constituye asesoría legal.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}